// src/pages/app-events.ts
import { LitElement, html, css, TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { getFirestore, collection, query, getDocs, orderBy } from "firebase/firestore";

type EventDoc = {
  event?: string;
  date?: any; // Firestore Timestamp
  time?: string; // "19:30"
  address?: string;
  notes?: string;
};

type EventItem = EventDoc & { id: string };

@customElement("app-events")
export class AppEvents extends LitElement {
  @state() private upcomingEvents: EventItem[] = [];
  @state() private isLoading = true;
  @state() private error: string | null = null;

  private db = getFirestore();

  static styles = css`
    :host {
      display: block;
      padding: 14px;
      max-width: 980px;
      margin: 0 auto;
      color: var(--text);
      background: var(--bg);
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Inter, Arial, sans-serif;
    }

    .first-stat {
      padding-top: 115px; /* match header spacing */
    }

    .top {
      position: sticky;
      top: 0;
      z-index: 5;
      padding: 12px;
      border-radius: var(--radius);
      border: 1px solid var(--border);
      background: var(--surface);
      box-shadow: var(--shadow);
      margin-bottom: 12px;
    }

    .titleRow {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    h2 {
      margin: 0;
      font-size: 18px;
      letter-spacing: 0.2px;
      color: var(--text);
    }

    .hint {
      margin-top: 8px;
      font-size: 12px;
      color: var(--muted);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .err {
      padding: 10px 12px;
      border-radius: var(--radius);
      border: 1px solid color-mix(in srgb, var(--error) 35%, var(--border) 65%);
      background: color-mix(in srgb, var(--error) 12%, var(--surface) 88%);
      color: var(--text);
      margin: 12px 0;
    }

    .month {
      margin-top: 14px;
      font-weight: 950;
      color: var(--text);
      opacity: 0.92;
      letter-spacing: 0.2px;
    }

    .card {
      position: relative;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      padding: 14px;
      border-radius: var(--radius);
      border: 1px solid var(--border);
      background: color-mix(in srgb, var(--surface) 92%, var(--text) 8%);
      margin-top: 10px;
      transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
    }

    .card:hover {
      transform: translateY(-1px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
      border-color: color-mix(in srgb, var(--primary) 35%, var(--border) 65%);
    }

    /* left rail */
    .card::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 6px;
      border-radius: var(--radius) 0 0 var(--radius);
      background: color-mix(in srgb, var(--primary) 70%, black 0%);
    }

    .left {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 0;
    }

    .title {
      font-size: 16px;
      font-weight: 950;
      color: var(--text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 620px;
    }

    .meta {
      font-size: 12px;
      color: var(--muted);
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      align-items: center;
    }

    .pill {
      font-size: 12px;
      font-weight: 900;
      padding: 6px 10px;
      border-radius: 999px;
      background: color-mix(in srgb, var(--surface) 85%, var(--text) 15%);
      border: 1px solid color-mix(in srgb, var(--border) 75%, var(--text) 25%);
      color: var(--text);
      white-space: nowrap;
    }

    .actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: flex-end;
      align-items: center;
      margin-left: 10px;
    }

    button.pill {
      appearance: none;
      cursor: pointer;
      line-height: 1;
      opacity: 0.95;
    }

    button.pill.action {
      background: var(--primary);
      border: 1px solid var(--primary);
      color: var(--primary-contrast);
    }

    button.pill.ghost {
      background: transparent;
      border: 1px solid color-mix(in srgb, var(--border) 70%, var(--text) 30%);
      color: var(--text);
    }

    button.pill:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .empty {
      color: var(--muted);
      padding: 12px;
      border: 1px dashed var(--border);
      border-radius: var(--radius);
      background: var(--surface);
      margin-top: 12px;
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.fetchUpcomingEvents();
  }

  private toMillis(ts: any): number {
    try {
      if (!ts) return Number.MAX_SAFE_INTEGER;
      if (ts?.toMillis) return ts.toMillis();
      if (ts?.toDate) return ts.toDate().getTime();
      if (ts instanceof Date) return ts.getTime();
      const n = new Date(ts).getTime();
      return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
    } catch {
      return Number.MAX_SAFE_INTEGER;
    }
  }

  private fmtDate(ts: any): string {
    const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
    if (!d) return "TBC";
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  }

  private fmtMonth(ts: any): string {
    const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
    if (!d) return "TBC";
    return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }

  private groupByMonth(items: EventItem[]) {
    const map = new Map<string, EventItem[]>();
    for (const e of items) {
      const key = this.fmtMonth(e.date);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    return Array.from(map.entries());
  }

  private async fetchUpcomingEvents() {
    this.isLoading = true;
    this.error = null;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const eventsRef = collection(this.db, "Events");
      const qy = query(eventsRef, orderBy("date", "asc"));
      const snap = await getDocs(qy);

      const seen = new Set<string>();
      const events: EventItem[] = [];

      snap.forEach((d) => {
        if (seen.has(d.id)) return;
        seen.add(d.id);

        const data = d.data() as any;
        const eventDateMs = this.toMillis(data.date);
        if (eventDateMs === Number.MAX_SAFE_INTEGER) return;

        if (eventDateMs >= today.getTime()) {
          events.push({
            id: d.id,
            ...(data as EventDoc),
          });
        }
      });

      events.sort((a, b) => this.toMillis(a.date) - this.toMillis(b.date));
      this.upcomingEvents = events;
    } catch (e: any) {
      this.error = e?.message ?? "Failed to load events.";
      this.upcomingEvents = [];
    } finally {
      this.isLoading = false;
    }
  }

  private mapLink(address?: string) {
    const a = (address ?? "").trim();
    if (!a) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a)}`;
  }

  private buildStartDateTime(event: EventItem): Date | null {
    const d = event.date?.toDate ? event.date.toDate() : event.date instanceof Date ? event.date : null;
    if (!d) return null;

    const time = (event.time ?? "").trim();
    if (!time) return d;

    const [hh, mm] = time.split(":").map((x) => parseInt(x, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return d;

    const dt = new Date(d);
    dt.setHours(hh, mm, 0, 0);
    return dt;
  }

  private toICSDate(dt: Date): string {
    // local time (simple). If you want strict UTC, we can do Z conversions later.
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    return `${yyyy}${mm}${dd}T${hh}${mi}00`;
  }

  private generateICS(event: EventItem) {
    const start = this.buildStartDateTime(event);
    if (!start) return;

    const end = new Date(start);
    end.setHours(end.getHours() + 4);

    const title = (event.event ?? "Club event").trim();
    const address = (event.address ?? "").trim();

    const icsData = `
BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//AppletonCC//Events//EN
BEGIN:VEVENT
SUMMARY:${title}
DESCRIPTION:${title}${address ? ` at ${address}` : ""}
DTSTART:${this.toICSDate(start)}
DTEND:${this.toICSDate(end)}
LOCATION:${address}
END:VEVENT
END:VCALENDAR
    `.trim();

    const blob = new Blob([icsData], { type: "text/calendar" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${title || "event"}.ics`.replace(/[^\w\- ]+/g, "").slice(0, 40) + ".ics";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private generateGoogleCalendarLink(event: EventItem) {
    const start = this.buildStartDateTime(event);
    if (!start) return;

    const end = new Date(start);
    end.setHours(end.getHours() + 4);

    const fmt = (dt: Date) => {
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      const hh = String(dt.getHours()).padStart(2, "0");
      const mi = String(dt.getMinutes()).padStart(2, "0");
      return `${yyyy}${mm}${dd}T${hh}${mi}00`;
    };

    const title = (event.event ?? "Club event").trim();
    const address = (event.address ?? "").trim();

    const url =
      `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(title)}` +
      `&dates=${fmt(start)}/${fmt(end)}` +
      `&details=${encodeURIComponent(title + (address ? ` at ${address}` : ""))}` +
      `&location=${encodeURIComponent(address)}`;

    window.open(url, "_blank");
  }

  private renderTop(): TemplateResult {
    return html`
      <div class="top">
        <div class="titleRow">
          <h2>Events</h2>
          ${this.isLoading ? html`<span class="pill">Loading…</span>` : null}
        </div>

        <div class="hint">
          <span>Upcoming club events.</span>
          <span></span>
        </div>
      </div>
    `;
  }

  render(): TemplateResult {
    return html`
      <app-header pageTitle="Events"></app-header>

      <div class="top-stats first-stat">
        ${this.renderTop()}
        ${this.error ? html`<div class="err">${this.error}</div>` : null}

        ${this.isLoading
          ? html`<div class="empty">Loading…</div>`
          : this.upcomingEvents.length === 0
            ? html`<div class="empty">No upcoming events.</div>`
            : this.groupByMonth(this.upcomingEvents).map(
                ([month, list]) => html`
                  <div class="month">${month}</div>

                  ${list.map((ev) => {
                    const title = (ev.event ?? "Event").trim();
                    const dateLabel = this.fmtDate(ev.date);
                    const timeLabel = (ev.time ?? "").trim();
                    const address = (ev.address ?? "").trim();
                    const map = this.mapLink(address);

                    return html`
                      <div class="card">
                        <div class="left">
                          <div class="title">${title}</div>

                          <div class="meta">
                            <span>${dateLabel}</span>
                            ${timeLabel ? html`<span class="pill">${timeLabel}</span>` : null}
                            ${address ? html`<span class="pill">${address}</span>` : null}
                          </div>
                        </div>

                        <div class="actions">
                          ${map
                            ? html`
                                <a class="pill ghost" href=${map} target="_blank" rel="noopener noreferrer">
                                  Maps
                                </a>
                              `
                            : null}

                          <button class="pill action" @click=${() => this.generateGoogleCalendarLink(ev)}>
                            Google
                          </button>

                          <button class="pill ghost" @click=${() => this.generateICS(ev)}>
                            ICS
                          </button>
                        </div>
                      </div>
                    `;
                  })}
                `
              )}
      </div>
    `;
  }
}
